import React from 'react';

interface Props {
  children: React.ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary so a render-time exception falls back to a visible
 * page instead of a blank screen. Renders a small recovery UI with the error
 * message + a hard-reload button. Console keeps the full stack for debugging.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-emerald-900 text-white flex items-center justify-center font-black tracking-tight">GV</div>
          <h1 className="text-xl font-extrabold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-500 text-sm mb-4">
            The page couldn't load. Please try reloading — if it keeps happening,
            give us a call and we'll take your order over the phone.
          </p>
          {this.state.error && (
            <pre className="text-left text-[11px] bg-gray-100 text-gray-700 rounded-lg p-3 overflow-auto max-h-32 mb-5 font-mono">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-emerald-900 hover:bg-emerald-950 text-white font-bold rounded-xl transition-colors"
            >
              Reload page
            </button>
            <a
              href="tel:+254790215267"
              className="w-full py-2.5 text-emerald-800 hover:text-emerald-900 text-sm font-semibold"
            >
              Call +254 790 215 267
            </a>
          </div>
        </div>
      </div>
    );
  }
}
